import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Plan data - easier to modify in one place
const PLANS = [
    {
        name: "Free Plan",
        price: 0,
        features: [
            "Simple Cover Letter Generator (5/month)",
            "5 AI Interview Questions/month",
            "Standard Resume Review",
            "Email Support (48h response)",
            "Job Search Tips & Resources",
            "Limited Usage"
        ],
        recommended: false,
        cta: "Get Started"
    },
    {
        name: "Premium Plan",
        price: 499,
        features: [
            "AI-Powered Cover Letters (Unlimited)",
            "Unlimited AI Interview Questions",
            "Live 1:1 Interview Coaching",
            "Priority 24/7 Support",
            "ATS-Optimized Resume & LinkedIn",
            "Personalized Job Matches",
            "Career Path Planning",
            "Salary Negotiation Guide",
            "Access to Exclusive Webinars",
            "Unlimited Usage"
        ],
        recommended: true,
        cta: "Subscribe Now"
    }
];

const SignupPricing = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [razorpayLoading, setRazorpayLoading] = useState(true);
    const [razorpayReady, setRazorpayReady] = useState(false);

    useEffect(() => {
        const checkRazorpay = () => {
            if (window.Razorpay) {
                setRazorpayReady(true);
                setRazorpayLoading(false);
            } else if (document.querySelector('script[src*="razorpay"]')) {
                // Script is still loading
                setTimeout(checkRazorpay, 100);
            } else {
                console.error('Razorpay script not found');
                setRazorpayLoading(false);
                setError('Payment system is unavailable. Please refresh the page.');
            }
        };

        checkRazorpay();
    }, []);

    // Add state for auth status
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check auth status on component mount
    useEffect(() => {
        const checkAuth = async () => {
            // Check if user is logged in (you can replace this with your actual auth check)
            const auth = localStorage.getItem('user_token'); // or your auth method
            setIsAuthenticated(!!auth);
        };
        checkAuth();
    }, []);

    const handlePlanSelection = (plan) => {
        if (!isAuthenticated) {
            try { localStorage.setItem('selected_plan', JSON.stringify(plan)); } catch {}
            navigate('/auth');
            return;
        }
        handlePayment(plan);
    };

    const handlePayment = async (plan) => {
        try {
            if (!razorpayReady) {
                setError('Payment system is not ready. Please refresh the page.');
                return;
            }

            setLoading(true);
            setError(null);

            // Create order
            const orderResponse = await axios.post(`${API_BASE_URL}/api/payments/create-order`, {
                amount: plan.price * 100, // Convert to paise
                currency: 'INR',
                receipt: `plan_${plan.name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
                notes: {
                    plan_name: plan.name
                }
            }); 
            const { order } = orderResponse.data.data; 
            const key_id = orderResponse?.data?.data?.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;

            // Initialize Razorpay
            const options = {
                key: key_id,
                amount: order.amount,
                currency: order.currency,
                name: 'Rakfort Career Services',
                description: `${plan.name} Subscription`,
                order_id: order.id,
                handler: async (response) => {
                    try {
                        // Verify payment on backend
                        await axios.post(`${API_BASE_URL}/api/payments/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        });

                        // Show success message
                        alert('Payment successful! Welcome to ' + plan.name);
                        navigate('/signuppricing'); // Or wherever you want to redirect after success
                    } catch (err) {
                        console.error('Payment verification failed:', err);
                        setError('Payment verification failed. Please contact support.');
                    }
                },
                prefill: {
                    name: '', // Can be pulled from user context if available
                    email: '',
                    contact: ''
                },
                theme: {
                    color: '#3C0F6C'
                },
                modal: {
                    ondismiss: () => {
                        setError('Payment cancelled.');
                    }
                }
            };

            const razorpay = new window.Razorpay(options);
            razorpay.on('payment.failed', function (response) {
                console.error('Payment failed:', response?.error);
                setError(response?.error?.description || 'Payment failed. Please try again.');
            });
            razorpay.open();
        } catch (err) {
            console.error('Payment initiation failed:', err);
            setError('Failed to initiate payment. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#1A0637] via-[#2C0B4F] to-[#1A0637] text-white py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Loading State */}
                {razorpayLoading && (
                    <div className="absolute top-4 right-4 bg-yellow-500/20 text-yellow-300 px-4 py-2 rounded-lg flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Initializing payment system...
                    </div>
                )}

                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-[#FFD600]">Choose Your Plan</h1>
                    <p className="text-lg text-gray-300 max-w-2xl mx-auto">Select the perfect plan to accelerate your career journey with our AI-powered tools</p>
                </div>

                {/* Plans Grid */}
                <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.name}
                            className={`relative bg-gradient-to-b ${plan.recommended 
                                ? 'from-[#3C0F6C] to-[#2C0B4F] border-2 border-[#FFD600] shadow-2xl shadow-[#FFD600]/20 hover:shadow-[#FFD600]/30' 
                                : 'from-white/5 to-white/[0.03] border border-white/10 hover:border-white/20 hover:shadow-lg hover:shadow-white/5'
                                } backdrop-blur-lg rounded-2xl p-8 transition-all duration-300 ease-in-out transform hover:scale-[1.02] cursor-pointer`}
                        >
                            {plan.recommended && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 transition-transform duration-300 group-hover:scale-105">
                                    <span className="bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6C] px-6 py-2 rounded-full text-sm font-bold shadow-lg shadow-[#FFD600]/30 flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        MOST POPULAR
                                    </span>
                                </div>
                            )}

                            <div className="text-center mb-8 transition-transform duration-300 group-hover:transform group-hover:scale-105">
                                <h3 className="text-2xl font-bold mb-2 group-hover:text-[#FFD600] transition-colors duration-300">{plan.name}</h3>
                                <div className="text-4xl font-bold mb-2">
                                    <span className="group-hover:text-[#FFD600] transition-colors duration-300">
                                        {plan.price === 0 ? 'Free' : `₹${plan.price}`}
                                    </span>
                                    {plan.price > 0 && (
                                        <span className="text-lg text-gray-300 group-hover:text-gray-400 transition-colors duration-300">/mo</span>
                                    )}
                                </div>
                                {plan.price === 0 && (
                                    <p className="text-sm text-gray-300 mb-4">No credit card required</p>
                                )}
                            </div>
                            <ul className="space-y-4 mb-8">
                                {plan.features.map((feature) => (
                                    <li key={feature} className="flex items-center group-hover:translate-x-1 transition-transform duration-200">
                                        <svg
                                            className={`h-5 w-5 ${plan.recommended ? 'text-[#FFD600]' : 'text-white/60'} mr-3 transition-transform duration-200 group-hover:scale-110`}
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2.5"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="transition-colors duration-200 group-hover:text-[#FFD600]">
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handlePlanSelection(plan)}
                                disabled={loading}
                                className={`w-full py-3 px-6 rounded-xl text-center font-semibold transition-all duration-300
                                    ${plan.recommended
                                    ? 'bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6F] hover:shadow-lg hover:shadow-[#FFD600]/30 transform hover:-translate-y-1 hover:brightness-110'
                                    : 'bg-white/10 border border-white/20 hover:bg-white/20 hover:border-white/30 hover:shadow-lg hover:shadow-white/5 transform hover:-translate-y-1'
                                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {loading ? 'Processing...' : (plan.cta || (plan.price > 0 ? 'Subscribe Now' : 'Get Started'))}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SignupPricing;